import { resolveLogoApiUrl, resolveLogoDisplayUrl, resolveReachOutImageFileUrl } from "@/lib/logoUpload";
import {
  resolveSelectedPropertyImageUrl,
  resolveSelectedPropertyImageUrlForApi,
} from "@/lib/propertyMedia";
import {
  findBranch,
  findLegalEntity,
  findRootLegalEntityForSelection,
} from "@/modules/subscription/billingAccess";
import type { LegalEntityHierarchy } from "@/services/profileService";
import { resolveReachOutTemplateImageUrl } from "@/services/reachoutService";

function pickLogoDisplayUrl(logoUrl: string | null | undefined): string | null {
  return resolveLogoDisplayUrl(logoUrl);
}

function pickLogoApiUrl(logoUrl: string | null | undefined): string | null {
  return resolveLogoApiUrl(logoUrl);
}

function resolveRootLegalEntity(
  legalEntities: LegalEntityHierarchy[],
  legalEntityId?: string | null,
  branchId?: string | null,
): LegalEntityHierarchy | null {
  if (legalEntities.length === 0) return null;
  return findRootLegalEntityForSelection(
    legalEntities,
    normalizeId(legalEntityId),
    normalizeId(branchId),
  );
}

/** Signed/displayable logo for the root legal entity only — no branch/LE fallback. */
export function resolveRootLegalEntityLogoUrl(
  legalEntities: LegalEntityHierarchy[],
  legalEntityId?: string | null,
  branchId?: string | null,
): string | null {
  const root = resolveRootLegalEntity(legalEntities, legalEntityId, branchId);
  return pickLogoDisplayUrl(root?.logoUrl);
}

/** Logo for the currently selected legal entity, including a nested sub-entity. */
export function resolveSelectedLegalEntityLogoUrl(
  legalEntities: LegalEntityHierarchy[],
  legalEntityId?: string | null,
): string | null {
  const selectedId = normalizeId(legalEntityId);
  if (!selectedId) return null;
  return pickLogoDisplayUrl(findLegalEntity(legalEntities, selectedId)?.logoUrl);
}

/**
 * Branch-scoped top bar: branch logo first, then the branch's parent legal entity.
 * Does not fall back to the org root when a nearer legal entity exists.
 */
export function resolveBranchScopeLogoUrl(
  legalEntities: LegalEntityHierarchy[],
  legalEntityId?: string | null,
  branchId?: string | null,
): string | null {
  const branchLogo = resolveBranchLogoUrl(legalEntities, branchId);
  if (branchLogo) return branchLogo;

  const resolvedLegalEntityId = resolveLegalEntityIdForLogo(
    legalEntities,
    legalEntityId,
    branchId,
  );
  if (!resolvedLegalEntityId) return null;

  const legalEntity = findLegalEntity(legalEntities, resolvedLegalEntityId);
  return pickLogoDisplayUrl(legalEntity?.logoUrl);
}

function normalizeId(id: string | null | undefined): string | null {
  return id?.trim() || null;
}

function resolveLegalEntityIdForLogo(
  legalEntities: LegalEntityHierarchy[],
  legalEntityId: string | null | undefined,
  branchId: string | null | undefined,
): string | null {
  const baseLegalEntityId = normalizeId(legalEntityId);
  const normalizedBranchId = normalizeId(branchId);
  if (!normalizedBranchId) return baseLegalEntityId;

  const hierarchyBranch = findBranch(legalEntities, normalizedBranchId);
  return normalizeId(hierarchyBranch?.legalEntityId) ?? baseLegalEntityId;
}

function resolveBranchLogoUrl(
  legalEntities: LegalEntityHierarchy[],
  branchId: string | null | undefined,
  branchSummaryLogoUrl?: string | null,
): string | null {
  const normalizedBranchId = normalizeId(branchId);
  if (!normalizedBranchId) return null;

  const hierarchyBranch = findBranch(legalEntities, normalizedBranchId);
  return (
    pickLogoDisplayUrl(hierarchyBranch?.logoUrl) ??
    pickLogoDisplayUrl(branchSummaryLogoUrl)
  );
}

function resolveBranchLogoApiUrl(
  legalEntities: LegalEntityHierarchy[],
  branchId: string | null | undefined,
  branchSummaryLogoUrl?: string | null,
): string | null {
  const normalizedBranchId = normalizeId(branchId);
  if (!normalizedBranchId) return null;

  const hierarchyBranch = findBranch(legalEntities, normalizedBranchId);
  return (
    pickLogoApiUrl(hierarchyBranch?.logoUrl) ?? pickLogoApiUrl(branchSummaryLogoUrl)
  );
}

export interface ResolveAgencyLogoUrlParams {
  legalEntities: LegalEntityHierarchy[];
  branchId?: string | null;
  legalEntityId?: string | null;
  branchSummaryLogoUrl?: string | null;
}

/**
 * Resolves the agency logo using:
 * branch → parent legal entity → root legal entity → Bridge default.
 */
export function resolveAgencyLogoUrl({
  legalEntities,
  branchId,
  legalEntityId,
  branchSummaryLogoUrl,
}: ResolveAgencyLogoUrlParams): string {
  const branchLogo = resolveBranchLogoUrl(
    legalEntities,
    branchId,
    branchSummaryLogoUrl,
  );
  if (branchLogo) return branchLogo;

  const resolvedLegalEntityId = resolveLegalEntityIdForLogo(
    legalEntities,
    legalEntityId,
    branchId,
  );

  if (resolvedLegalEntityId) {
    const legalEntity = findLegalEntity(legalEntities, resolvedLegalEntityId);
    const legalEntityLogo = pickLogoDisplayUrl(legalEntity?.logoUrl);
    if (legalEntityLogo) return legalEntityLogo;
  }

  const rootLegalEntity = findRootLegalEntityForSelection(
    legalEntities,
    resolvedLegalEntityId,
    normalizeId(branchId),
  );
  const rootLogo = pickLogoDisplayUrl(rootLegalEntity?.logoUrl);
  if (rootLogo) return rootLogo;

  return resolveReachOutTemplateImageUrl();
}

/** Agency logo URL for API payloads — skips legacy base64 logos. */
export function resolveAgencyLogoUrlForApi({
  legalEntities,
  branchId,
  legalEntityId,
  branchSummaryLogoUrl,
}: ResolveAgencyLogoUrlParams): string {
  const branchLogo = resolveBranchLogoApiUrl(
    legalEntities,
    branchId,
    branchSummaryLogoUrl,
  );
  if (branchLogo) return branchLogo;

  const resolvedLegalEntityId = resolveLegalEntityIdForLogo(
    legalEntities,
    legalEntityId,
    branchId,
  );

  if (resolvedLegalEntityId) {
    const legalEntity = findLegalEntity(legalEntities, resolvedLegalEntityId);
    const legalEntityLogo = pickLogoApiUrl(legalEntity?.logoUrl);
    if (legalEntityLogo) return legalEntityLogo;
  }

  const rootLegalEntity = findRootLegalEntityForSelection(
    legalEntities,
    resolvedLegalEntityId,
    normalizeId(branchId),
  );
  const rootLogo = pickLogoApiUrl(rootLegalEntity?.logoUrl);
  if (rootLogo) return rootLogo;

  return resolveReachOutTemplateImageUrl();
}

export interface ResolveReachOutHeaderImageUrlParams {
  propertyId: string | null | undefined;
  propertyDetail: unknown;
  propertySummary: unknown;
  previewImageUrlsByPropertyId?: Record<string, string[]>;
  legalEntities: LegalEntityHierarchy[];
  branchId?: string | null;
  legalEntityId?: string | null;
  branchSummaryLogoUrl?: string | null;
}

/** Reach Out live preview: property photo first, then agency logo fallback. */
export function resolveReachOutHeaderImageUrl(
  params: ResolveReachOutHeaderImageUrlParams,
): string | null {
  return resolveReachOutHeaderImageUrlInternal(params, "display");
}

/** Reach Out publish payload: persistent file URLs only — never signed or base64 URLs. */
export function resolveReachOutHeaderImageUrlForApi(
  params: ResolveReachOutHeaderImageUrlParams,
): string | null {
  const resolved = resolveReachOutHeaderImageUrlInternal(params, "api");
  return resolveReachOutImageFileUrl(resolved);
}

function resolveReachOutHeaderImageUrlInternal(
  params: ResolveReachOutHeaderImageUrlParams,
  mode: "display" | "api",
): string | null {
  const {
    propertyId,
    propertyDetail,
    propertySummary,
    previewImageUrlsByPropertyId,
    legalEntities,
    branchId,
    legalEntityId,
    branchSummaryLogoUrl,
  } = params;

  const propertyImageUrl =
    mode === "api"
      ? resolveSelectedPropertyImageUrlForApi({
          propertyId,
          propertyDetail,
          propertySummary,
          previewImageUrlsByPropertyId,
        })
      : resolveSelectedPropertyImageUrl({
          propertyId,
          propertyDetail,
          propertySummary,
          previewImageUrlsByPropertyId,
        });
  if (propertyImageUrl) return propertyImageUrl;

  if (!normalizeId(branchId)) return null;

  return mode === "api"
    ? resolveAgencyLogoUrlForApi({
        legalEntities,
        branchId,
        legalEntityId,
        branchSummaryLogoUrl,
      })
    : resolveAgencyLogoUrl({
        legalEntities,
        branchId,
        legalEntityId,
        branchSummaryLogoUrl,
      });
}
